import { Injectable } from '@angular/core';
import { UserDetailsEntity, UserSecurityRoles, UserFeatures, UserAuthorizedRoles, UserAuthorizedFeatures } from 'private/app/models/user.model';

const SYSTEM_DEFAULTS_CATEGORY = 'System Defaults';
const SYSTEM_DEFAULTS_CATEGORY_ID = 7;

@Injectable({ providedIn: 'root' })
export class EditUserSecurityHelperService {
    initializeSelections(details: UserDetailsEntity, authorizedRoles: UserAuthorizedRoles[], authorizedFeatures: UserAuthorizedFeatures[]) {
        const selectedRoleIds = new Set<number>();
        const selectedFeatureIds = new Set<number>();

        const authorizedRoleIds = new Set(authorizedRoles.flatMap((authRole) => authRole.userRoles).map((rol) => rol.roleId));

        const roleFeatureIds = new Set<number>();

        details.userRolesDetails.forEach((role) => {
            if (role.roleCategoryId !== SYSTEM_DEFAULTS_CATEGORY_ID && authorizedRoleIds.has(role.roleId)) {
                selectedRoleIds.add(role.roleId);
            }

            role.userFeatures?.forEach((feature) => {
                roleFeatureIds.add(feature.featureId);
            });
        });

        this.getDefaultRoles(details, authorizedRoles).forEach((role) => {
            role.userFeatures?.forEach((feature) => {
                roleFeatureIds.add(feature.featureId);
            });
        });

        const authorizedFeatureIds = new Set(authorizedFeatures.flatMap((authFtr) => authFtr.userFeatures).map((ftr) => ftr.featureId));

        details.additionalFeatures?.forEach((feature) => {
            if (!roleFeatureIds.has(feature.featureId) && authorizedFeatureIds.has(feature.featureId)
            ) {
                selectedFeatureIds.add(feature.featureId);
            }
        });

        return {
            selectedRoleIds,
            selectedFeatureIds
        };
    }

    getRoleFeatureIds(details: UserDetailsEntity, authorizedRoles: UserAuthorizedRoles[], selectedRoleIds: Set<number>): Set<number> {
        const featureIds = new Set<number>();
        const selectedRoles = authorizedRoles.flatMap((rol) => rol.userRoles).filter((role) => selectedRoleIds.has(role.roleId));
        const authorizedRoleIds = new Set(authorizedRoles.flatMap((rol) => rol.userRoles).map((role) => role.roleId));

        const defaultRoles = this.getDefaultRoles(details, authorizedRoles);
        const restrictedRoles = details.userRolesDetails.filter(
            (role) => role.roleCategoryId !== SYSTEM_DEFAULTS_CATEGORY_ID && !authorizedRoleIds.has(role.roleId)
        );

        [...defaultRoles, ...restrictedRoles, ...selectedRoles].forEach((role) => {
            role.userFeatures?.forEach((feature) => {
                featureIds.add(feature.featureId);
            });
        });

        return featureIds;
    }

    removeConflictingFeatures(selectedFeatureIds: Set<number>, roleFeatureIds: Set<number>): Set<number> {
        return new Set([...selectedFeatureIds].filter((id) => !roleFeatureIds.has(id)));
    }

    buildSummary(details: UserDetailsEntity, authorizedRoles: UserAuthorizedRoles[], authorizedFeatures: UserAuthorizedFeatures[], selectedRoleIds: Set<number>, selectedFeatureIds: Set<number>) {
        const summary = {
            defaultRoles: [] as UserSecurityRoles[],
            selectedRoles: [] as UserSecurityRoles[],
            roleFeatures: [] as UserFeatures[],
            additionalFeatures: [] as UserFeatures[]
        };

        const roleFeatureMap = new Map<number, UserFeatures>();
        const authorizedRoleIds = new Set(authorizedRoles.flatMap((g) => g.userRoles).map((r) => r.roleId));

        summary.defaultRoles = this.getDefaultRoles(details, authorizedRoles);
        summary.defaultRoles.forEach((role) => {
            role.userFeatures?.forEach((feature) => {
                roleFeatureMap.set(feature.featureId, feature);
            });
        });

        details.userRolesDetails.forEach((role) => {
            if (role.roleCategoryId !== SYSTEM_DEFAULTS_CATEGORY_ID && !authorizedRoleIds.has(role.roleId)) {
                summary.selectedRoles.push(role);

                role.userFeatures?.forEach((feature) => {
                    roleFeatureMap.set(feature.featureId, feature);
                });
            }
        });

        authorizedRoles.flatMap((g) => g.userRoles).forEach((role) => {
            if (selectedRoleIds.has(role.roleId)) {
                summary.selectedRoles.push(role);
                role.userFeatures?.forEach((feature) => {
                    roleFeatureMap.set(feature.featureId, feature);
                });
            }
        });

        summary.roleFeatures = Array.from(roleFeatureMap.values());

        const additionalMap = new Map<number, UserFeatures>();

        const authorizedFeatureIds = new Set(authorizedFeatures.flatMap((g) => g.userFeatures).map((ftr) => ftr.featureId));

        details.additionalFeatures?.forEach((feature) => {
            if (roleFeatureMap.has(feature.featureId)) {
                return;
            }

            if (!authorizedFeatureIds.has(feature.featureId)) {
                additionalMap.set(feature.featureId, feature);
            }
        });

        authorizedFeatures.flatMap((g) => g.userFeatures).forEach((feature) => {
            if (selectedFeatureIds.has(feature.featureId) && !roleFeatureMap.has(feature.featureId)) {
                additionalMap.set(feature.featureId, feature);
            }
        });

        summary.additionalFeatures = Array.from(additionalMap.values());

        summary.defaultRoles.sort((a, b) => (a.roleName || '').localeCompare(b.roleName || ''));
        summary.selectedRoles.sort((a, b) => (a.roleName || '').localeCompare(b.roleName || ''));
        summary.roleFeatures.sort((a, b) => (a.featureName || '').localeCompare(b.featureName || ''));
        summary.additionalFeatures.sort((a, b) => (a.featureName || '').localeCompare(b.featureName || ''));

        return summary;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    buildSubmitPayload(details: UserDetailsEntity, summary: any): UserDetailsEntity {
        const payload = structuredClone(details);

        payload.userRolesDetails = [
            ...summary.defaultRoles,
            ...summary.selectedRoles
        ];

        payload.additionalFeatures = summary.additionalFeatures;

        return payload;
    }

    getDefaultRoles(details: UserDetailsEntity, authorizedRoles: UserAuthorizedRoles[]): UserSecurityRoles[] {
        const defaultsFromDetails = details.userRolesDetails.filter((role) => role.roleCategoryId === SYSTEM_DEFAULTS_CATEGORY_ID);

        if (defaultsFromDetails.length > 0) {
            return defaultsFromDetails;
        }

        return authorizedRoles
            .find((category) => category.roleCategory === SYSTEM_DEFAULTS_CATEGORY)
            ?.userRoles
            .map((role) => ({
                ...role,
                roleCategoryId: SYSTEM_DEFAULTS_CATEGORY_ID
            })) || [];
    }
}
